import { resolveGuardianEntry } from '@/features/guardian/utils/resolve-guardian-entry';
import { navigationRef } from '@/navigations/navigation-ref';
import type { AuthStackParamList, RootStackParamList } from '@/navigations/types';
import { useSessionStore, type UserRole } from '@/stores/session-store';
import { CommonActions, type NavigatorScreenParams } from '@react-navigation/native';

/**
 * Default landing inside the coach `Auth` tree: Drawer → Tabs → Home → Dashboard.
 * Shared so `withUnAuth`, Splash and role-switching all reset to the same place.
 */
export const AUTH_HOME_PARAMS: NavigatorScreenParams<AuthStackParamList> = {
  screen: 'AuthDrawer',
  params: {
    screen: 'HomeTabNavigator',
    params: { screen: 'Home', params: { screen: 'Dashboard' } },
  },
};

export type PostAuthTarget =
  | { screen: 'RoleSelect'; params?: undefined }
  | { screen: 'Auth'; params: RootStackParamList['Auth'] }
  | { screen: 'Guardian'; params: RootStackParamList['Guardian'] };

/**
 * Decide which root screen a signed-in user should land on, from their roles.
 *
 * - more than one role and none chosen yet → `RoleSelect`
 * - active (or single) role is guardian → `Guardian` (lands on Onboarding)
 * - otherwise (coach / default) → `Auth`
 */
export function resolvePostAuthTarget(
  roles: UserRole[],
  activeRole: UserRole | null,
): PostAuthTarget {
  if (roles.length > 1 && !activeRole) {
    return { screen: 'RoleSelect' };
  }

  const role = activeRole ?? roles[0] ?? 'coach';

  if (role === 'guardian') {
    return { screen: 'Guardian', params: { screen: 'Onboarding' } };
  }

  return { screen: 'Auth', params: AUTH_HOME_PARAMS };
}

/** Resolve the target from the current session store and reset the root stack to it. */
export async function resetRootToPostAuthTarget() {
  if (!navigationRef.isReady()) return;
  const { roles, activeRole } = useSessionStore.getState();
  const target = resolvePostAuthTarget(roles, activeRole);

  // Guardians are gated on their onboarding progress (resume vs. dashboard),
  // which needs the profile — resolve the in-stack target before resetting.
  const params = target.screen === 'Guardian' ? await resolveGuardianEntry() : target.params;

  navigationRef.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: target.screen, params }],
    }),
  );
}
